import { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc 
} from 'firebase/firestore';
import { db } from '../firebase/firebaseconfig';
import { FaUser, FaTools, FaUserShield, FaCheck, FaTimes, FaTrash, FaSearch, FaEdit, FaCar } from 'react-icons/fa';
import { useAuthGuard } from '../hooks/useAuthGuard';
import SparePartsManagement from './SparePartsManagement';

const AdminDashboard = () => {
  useAuthGuard('admin');
  const [users, setUsers] = useState([]);
  const [serviceOwners, setServiceOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('serviceOwners');
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const currentDate = new Date().toISOString();

  // Fetch users and service owners
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch service owners
        const serviceOwnersQuery = query(
          collection(db, 'users'),
          where('role', '==', 'serviceOwner')
        );
        const serviceOwnersSnapshot = await getDocs(serviceOwnersQuery);
        const serviceOwnersData = serviceOwnersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setServiceOwners(serviceOwnersData);
        
        // Fetch regular users
        const usersQuery = query(
          collection(db, 'users'),
          where('role', '==', 'user')
        );
        const usersSnapshot = await getDocs(usersQuery);
        const usersData = usersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setUsers(usersData);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load users data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle verification status update for service owners
  const handleVerificationUpdate = async (userId, isVerified) => {
    try {
      setStatusUpdateLoading(true);
      
      await updateDoc(doc(db, 'users', userId), {
        verified: isVerified,
        verificationDate: isVerified ? currentDate : null,
        updatedAt: currentDate,
        updatedBy: 'Pasans19' // Using current user's login
      });
      
      // Update local state
      setServiceOwners(prevOwners => 
        prevOwners.map(owner => 
          owner.id === userId ? { ...owner, verified: isVerified } : owner
        )
      );
    } catch (err) {
      console.error('Error updating verification status:', err);
      setError('Failed to update verification status');
    } finally {
      setStatusUpdateLoading(false);
    }
  };
  
  // Handle user deletion
  const handleDeleteUser = async (userId, userType) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'users', userId));
        
        // Update local state
        if (userType === 'serviceOwner') {
          setServiceOwners(prevOwners => 
            prevOwners.filter(owner => owner.id !== userId)
          );
        } else {
          setUsers(prevUsers => 
            prevUsers.filter(user => user.id !== userId)
          );
        }
      } catch (err) {
        console.error('Error deleting user:', err);
        setError('Failed to delete user');
      }
    }
  };

  // Handle edit user
  const handleEditUser = (user) => {
    setSelectedUser(user);
    setFormData({
      displayName: user.displayName || '',
      email: user.email || '',
      status: user.status || 'active',
      ...(user.role === 'serviceOwner' ? {
        businessName: user.businessName || '',
        businessAddress: user.businessAddress || '',
        verified: user.verified || false
      } : {})
    });
    setIsEditing(true);
  };

  // Handle form changes
  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const updateData = {
        displayName: formData.displayName,
        status: formData.status,
        updatedAt: currentDate,
        updatedBy: 'Pasans19'
      };
      
      if (selectedUser.role === 'serviceOwner') {
        updateData.businessName = formData.businessName;
        updateData.businessAddress = formData.businessAddress;
        updateData.verified = formData.verified;
      }
      
      await updateDoc(doc(db, 'users', selectedUser.id), updateData);
      
      // Update local state
      if (selectedUser.role === 'serviceOwner') {
        setServiceOwners(prevOwners => 
          prevOwners.map(owner => 
            owner.id === selectedUser.id ? { ...owner, ...updateData } : owner
          )
        );
      } else {
        setUsers(prevUsers => 
          prevUsers.map(user => 
            user.id === selectedUser.id ? { ...user, ...updateData } : user
          )
        );
      }
      
      setIsEditing(false);
      setSelectedUser(null);
    } catch (err) {
      console.error('Error updating user:', err);
      setError('Failed to update user');
    }
  };

  // Filter users based on search term
  const filteredServiceOwners = serviceOwners.filter(owner => 
    owner.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    owner.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    owner.businessName?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const filteredUsers = users.filter(user => 
    user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">Loading dashboard data...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="mt-2 text-gray-600">Manage users, service providers, and spare parts</p>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('serviceOwners')}
              className={`${
                activeTab === 'serviceOwners'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <FaTools className="mr-2" />
              Service Owners ({serviceOwners.length})
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`${
                activeTab === 'users'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <FaCar className="mr-2" />
              Vehicle Owners ({users.length})
            </button>
            <button
              onClick={() => setActiveTab('spareParts')}
              className={`${
                activeTab === 'spareParts'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <FaTools className="mr-2" />
              Spare Parts
            </button>
          </nav>
        </div>

        {/* Search - Only show for users and service owners tabs */}
        {activeTab !== 'spareParts' && (
          <div className="mb-6">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={`Search ${activeTab === 'serviceOwners' ? 'service owners' : 'users'}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>
        )}

        {/* Content Area */}
        <div className="bg-white shadow overflow-hidden rounded-md">
          {/* Service Owners Table */}
          {activeTab === 'serviceOwners' && (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Owner Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Business Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredServiceOwners.length > 0 ? (
                  filteredServiceOwners.map((owner) => (
                    <tr key={owner.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center">
                            <FaUser className="text-gray-500" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {owner.displayName}
                            </div>
                            <div className="text-sm text-gray-500">{owner.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{owner.businessName}</div>
                        <div className="text-sm text-gray-500">{owner.businessAddress}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          owner.verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {owner.verified ? 'Verified' : 'Pending Verification'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditUser(owner)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <FaEdit />
                          </button>
                          {!owner.verified ? (
                            <button
                              onClick={() => handleVerificationUpdate(owner.id, true)}
                              disabled={statusUpdateLoading}
                              className="text-green-600 hover:text-green-900"
                              title="Verify"
                            >
                              <FaCheck />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleVerificationUpdate(owner.id, false)}
                              disabled={statusUpdateLoading}
                              className="text-yellow-600 hover:text-yellow-900"
                              title="Revoke Verification"
                            >
                              <FaTimes />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteUser(owner.id, 'serviceOwner')}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                      No service owners found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {/* Users Table */}
          {activeTab === 'users' && (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Join Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center">
                            <FaUser className="text-gray-500" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.displayName}
                            </div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {user.status || 'Active'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.id, 'user')}
                            className="text-red-600 hover:text-red-900"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {/* Spare Parts Management */}
          {activeTab === 'spareParts' && <SparePartsManagement />}
        </div>

        {/* Edit User Modal */}
        {isEditing && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center">
                <FaUserShield className="mr-2" />
                Edit {selectedUser.role === 'serviceOwner' ? 'Service Owner' : 'User'}
              </h2>
              
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="displayName" className="block text-sm font-medium text-gray-700">
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="displayName"
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      disabled
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-50 sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
                  </div>
                  
                  {selectedUser.role === 'serviceOwner' && (
                    <>
                      <div>
                        <label htmlFor="businessName" className="block text-sm font-medium text-gray-700">
                          Business Name
                        </label>
                        <input
                          type="text"
                          id="businessName"
                          name="businessName"
                          value={formData.businessName}
                          onChange={handleChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="businessAddress" className="block text-sm font-medium text-gray-700">
                          Business Address
                        </label>
                        <textarea
                          id="businessAddress"
                          name="businessAddress"
                          rows="3"
                          value={formData.businessAddress}
                          onChange={handleChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        />
                      </div>
                      
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="verified"
                          name="verified"
                          checked={formData.verified}
                          onChange={handleChange}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="verified" className="ml-2 block text-sm text-gray-900">
                          Verified Business
                        </label>
                      </div>
                    </>
                  )}
                  
                  <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                      Account Status
                    </label>
                    <select
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    >
                      <option value="active">Active</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                  
                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditing(false);
                        setSelectedUser(null);
                      }}
                      className="bg-gray-200 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="bg-blue-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;